import { useCallback, useContext, useEffect, useReducer, useRef } from 'react';

import { match, P } from 'ts-pattern';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { SharedFeaturesInstrumentationIdContext } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import { useUFOStreamingExperience } from '@atlassian/ufo-experimental';

import { voiceDictationReducer } from '../effects/voiceDictationReducer';
import { WebSpeechToTextManager } from '../effects/WebSpeechToTextManager';
import {
	type VoiceDictationResult,
	VoiceDictationState,
	VoiceDictationStopReason,
	type WebSpeechToTextTraceAction,
} from '../effects/WebSpeechToTextModels';

export const useVoiceDictation = (
	onVoiceDictationUpdate: (result: VoiceDictationResult) => void,
	cloudID: string,
	{
		audioBitsPerSecond = 16000,
		sampleRate = 24000,
		timeSlice = 1000,
		maxRecordingDurationMs = 60000,
		enableGhostText: enableGhostTextOption,
	}: {
		audioBitsPerSecond?: number;
		sampleRate?: number;
		timeSlice?: number;
		maxRecordingDurationMs?: number;
		enableGhostText?: boolean;
	} = {},
): [
	VoiceDictationResult,
	{
		startRecording: () => void;
		stopRecording: (stopReason?: VoiceDictationStopReason) => void;
		enableGhostText: boolean;
	},
] => {
	const { sharedFeaturesInstrumentationId, isInSmartCreateFlow } = useContext(
		SharedFeaturesInstrumentationIdContext,
	);
	let enableGhostText =
		enableGhostTextOption ??
		FeatureGates.getExperimentValue('ai_speech_to_text_perf_enhancement', 'isEnabled', false);

	const {
		trackAIInteractionInit,
		trackAIResultView,
		trackAIResultError,
		trackAIInteractionDismiss,
		trackAIResultAction,
		trackAIResultDiscarded,
	} = useAIEventsInstrumentation({
		product: 'platform',
		subproduct: 'platform',
		aiFeatureName: 'aiVoiceDictation',
		proactiveGeneratedAI: 0,
		userGeneratedAI: 1,
		enablePlatformEvents: true,
		additionalAttributes: {
			productAttributionOverride: 'confluence',
			sharedFeaturesInstrumentationId,
			isInSmartCreateFlow,
		},
	});

	const {
		start,
		success,
		startConnectionAttempt,
		successConnectionEstablished,
		reportTTFB,
		reportRTT,
		failure,
	} = useUFOStreamingExperience('streaming-stt', 'ai-speech-to-text');

	const [result, dispatch] = useReducer(voiceDictationReducer, {
		state: VoiceDictationState.Uninitialized,
		transcription: null,
		ghostTranscription: null,
	});

	const trace = useCallback(
		(action: WebSpeechToTextTraceAction) => {
			match(action)
				.with({ type: 'START_WEBSOCKET_CONNECTION' }, () => {
					start();
					startConnectionAttempt();
				})
				.with({ type: 'WEBSOCKET_CONNECTION_SUCCESS' }, () => {
					successConnectionEstablished();
				})
				.with({ type: 'TRANSCRIPTION_ITEM_CREATED', payload: { createdItemChunkIndex: 1 } }, () => {
					// Time to First Byte for first chunk
					reportTTFB();
				})
				.with(
					{
						type: 'TRANSCRIPTION_ITEM_CREATED',
						payload: { createdItemChunkIndex: P.number },
					},
					({ payload: { createdItemChunkIndex } }) => {
						// Round-trip time between received server messages
						reportRTT(createdItemChunkIndex);
					},
				)
				.with({ type: 'STOP_RECORDING' }, (stopAction) => {
					success({ metadata: { stopReason: stopAction.payload.stopReason } });
				})
				.with({ type: 'ERROR' }, (errorAction) => {
					failure({ metadata: { error: errorAction.payload.error } });
				})
				.exhaustive();
		},
		[
			reportRTT,
			reportTTFB,
			start,
			startConnectionAttempt,
			successConnectionEstablished,
			failure,
			success,
		],
	);

	const speechToTextManager = useRef(
		new WebSpeechToTextManager({
			onTranscription: (action) => dispatch(action),
			onTrace: (action) => trace(action),
			cloudID,
			enableGhostText,
			sampleRate,
			audioBitsPerSecond,
			timeSlice,
			maxRecordingDurationMs,
		}),
	);

	useEffect(() => {
		onVoiceDictationUpdate(result);
	}, [result, onVoiceDictationUpdate]);

	useEffect(() => {
		match(result)
			.with({ state: VoiceDictationState.Initializing }, () => trackAIInteractionInit())
			.with({ state: VoiceDictationState.Recording, chunkIndex: 1 }, () => trackAIResultView())
			.with(
				{
					state: VoiceDictationState.Stopped,
					stopReason: VoiceDictationStopReason.StoppedAccepted,
				},
				() => trackAIResultAction('STTAcceptedByUser'),
			)
			.with(
				{
					state: VoiceDictationState.Stopped,
					stopReason: VoiceDictationStopReason.StoppedRejected,
				},
				() => trackAIResultDiscarded('STTRejectedByUser'),
			)
			.with(
				{
					state: VoiceDictationState.Stopped,
					stopReason: VoiceDictationStopReason.TimeoutAccepted,
				},
				() => trackAIResultAction('STTAcceptedDueToTimeout'),
			)
			.with({ state: VoiceDictationState.Error }, (errorResult) =>
				trackAIResultError({ aiErrorMessage: errorResult.error }),
			)
			.with(P._, () => {})
			.exhaustive();
	}, [
		result,
		trackAIInteractionInit,
		trackAIInteractionDismiss,
		trackAIResultView,
		trackAIResultError,
		trackAIResultAction,
		trackAIResultDiscarded,
	]);

	const startRecording = useCallback(() => {
		speechToTextManager.current.startRecording();
	}, []);

	const stopRecording = useCallback(
		(stopReason: VoiceDictationStopReason = VoiceDictationStopReason.StoppedAccepted) => {
			speechToTextManager.current.stopRecording(stopReason);
		},
		[],
	);

	useEffect(() => {
		return () => stopRecording();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return [result, { startRecording, stopRecording, enableGhostText }];
};
